import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
    LearningStore,
    UpdateProgressDto,
    VocabularyWithProgress,
    ReviewVocabularyDto,
    CreateNoteDto,
    UpdateNoteDto,
} from '../types/learning';
import { learningService } from '../services/learningService';

/**
 * Zustand store for student learning features
 *
 * Manages learning state including progress tracking, vocabulary learning
 * with spaced repetition, and notes management across all 7 learning modes.
 */
export const useLearningStore = create<LearningStore>()(
    devtools(
        persist(
            (set, get) => ({
                // ============================================
                // State
                // ============================================

                // Progress
                currentCourseId: null,
                progress: null,
                progressLoading: false,
                progressError: null,

                // Vocabulary
                vocabularyList: [],
                reviewQueue: [],
                vocabularyStats: null,
                vocabularyLoading: false,
                vocabularyError: null,

                // Notes
                notes: [],
                notesLoading: false,
                notesError: null,

                // Current Mode
                currentMode: 'full_text',

                // ============================================
                // Actions - Course & Progress
                // ============================================

                setCurrentCourse: (courseId: string) => {
                    set({ currentCourseId: courseId });
                },

                fetchProgress: async (courseId: string, episodeId?: string) => {
                    set({ progressLoading: true, progressError: null });

                    try {
                        const progress = await learningService.getProgress(courseId, episodeId);
                        set({
                            progress,
                            progressLoading: false,
                            progressError: null,
                        });
                    } catch (error) {
                        set({
                            progressLoading: false,
                            progressError: error instanceof Error ? error.message : 'Failed to fetch progress',
                        });
                        throw error;
                    }
                },

                updateProgress: async (courseId: string, updateDto: UpdateProgressDto) => {
                    try {
                        const progress = await learningService.updateProgress(courseId, updateDto);
                        set({ progress });
                    } catch (error) {
                        set({
                            progressError: error instanceof Error ? error.message : 'Failed to update progress',
                        });
                        throw error;
                    }
                },

                // ============================================
                // Actions - Vocabulary
                // ============================================

                fetchVocabulary: async (courseId: string, filters?) => {
                    set({ vocabularyLoading: true, vocabularyError: null });

                    try {
                        const vocabularyList = await learningService.getVocabulary(courseId, filters);
                        set({
                            vocabularyList,
                            vocabularyLoading: false,
                            vocabularyError: null,
                        });
                    } catch (error) {
                        set({
                            vocabularyLoading: false,
                            vocabularyError:
                                error instanceof Error ? error.message : 'Failed to fetch vocabulary',
                        });
                        throw error;
                    }
                },

                fetchReviewQueue: async (courseId: string, limit = 20) => {
                    set({ vocabularyLoading: true, vocabularyError: null });

                    try {
                        const reviewQueue = await learningService.getReviewQueue(courseId, limit);
                        set({
                            reviewQueue,
                            vocabularyLoading: false,
                            vocabularyError: null,
                        });
                    } catch (error) {
                        set({
                            vocabularyLoading: false,
                            vocabularyError:
                                error instanceof Error ? error.message : 'Failed to fetch review queue',
                        });
                        throw error;
                    }
                },

                fetchVocabularyStats: async (courseId: string) => {
                    try {
                        const vocabularyStats = await learningService.getVocabularyStats(courseId);
                        set({ vocabularyStats });
                    } catch (error) {
                        set({
                            vocabularyError:
                                error instanceof Error ? error.message : 'Failed to fetch vocabulary stats',
                        });
                        throw error;
                    }
                },

                reviewVocabulary: async (vocabularyId: string, reviewDto: ReviewVocabularyDto) => {
                    try {
                        const result = await learningService.reviewVocabulary(vocabularyId, reviewDto);

                        // Update local state
                        const { vocabularyList, reviewQueue } = get();

                        // Update vocabulary list
                        const updatedList = vocabularyList.map((vocab) =>
                            vocab.id === vocabularyId
                                ? {
                                      ...vocab,
                                      userProgress: {
                                          ...vocab.userProgress,
                                          masteryLevel: result.newMasteryLevel,
                                          nextReviewAt: result.nextReviewAt,
                                          reviewCount: vocab.userProgress.reviewCount + 1,
                                          lastReviewedAt: new Date(),
                                      },
                                  }
                                : vocab
                        );

                        // Remove from review queue
                        const updatedQueue = reviewQueue.filter((vocab) => vocab.id !== vocabularyId);

                        set({
                            vocabularyList: updatedList,
                            reviewQueue: updatedQueue,
                        });
                    } catch (error) {
                        set({
                            vocabularyError:
                                error instanceof Error ? error.message : 'Failed to review vocabulary',
                        });
                        throw error;
                    }
                },

                toggleFavorite: async (vocabularyId: string) => {
                    try {
                        const result = await learningService.toggleFavorite(vocabularyId);

                        // Update local state
                        const { vocabularyList, reviewQueue } = get();

                        const updateVocab = (vocab: VocabularyWithProgress) =>
                            vocab.id === vocabularyId
                                ? {
                                      ...vocab,
                                      userProgress: {
                                          ...vocab.userProgress,
                                          isFavorite: result.isFavorite,
                                      },
                                  }
                                : vocab;

                        set({
                            vocabularyList: vocabularyList.map(updateVocab),
                            reviewQueue: reviewQueue.map(updateVocab),
                        });
                    } catch (error) {
                        set({
                            vocabularyError:
                                error instanceof Error ? error.message : 'Failed to toggle favorite',
                        });
                        throw error;
                    }
                },

                // ============================================
                // Actions - Notes
                // ============================================

                fetchNotes: async (courseId: string, filters?) => {
                    set({ notesLoading: true, notesError: null });

                    try {
                        const notes = await learningService.getNotes(courseId, filters);
                        set({
                            notes,
                            notesLoading: false,
                            notesError: null,
                        });
                    } catch (error) {
                        set({
                            notesLoading: false,
                            notesError: error instanceof Error ? error.message : 'Failed to fetch notes',
                        });
                        throw error;
                    }
                },

                createNote: async (noteDto: CreateNoteDto) => {
                    try {
                        const newNote = await learningService.createNote(noteDto);
                        const { notes } = get();
                        set({ notes: [newNote, ...notes] });
                        return newNote;
                    } catch (error) {
                        set({
                            notesError: error instanceof Error ? error.message : 'Failed to create note',
                        });
                        throw error;
                    }
                },

                updateNote: async (noteId: string, updateDto: UpdateNoteDto) => {
                    try {
                        const updatedNote = await learningService.updateNote(noteId, updateDto);
                        const { notes } = get();
                        set({
                            notes: notes.map((note) => (note.id === noteId ? updatedNote : note)),
                        });
                        return updatedNote;
                    } catch (error) {
                        set({
                            notesError: error instanceof Error ? error.message : 'Failed to update note',
                        });
                        throw error;
                    }
                },

                deleteNote: async (noteId: string) => {
                    try {
                        await learningService.deleteNote(noteId);
                        const { notes } = get();
                        set({ notes: notes.filter((note) => note.id !== noteId) });
                    } catch (error) {
                        set({
                            notesError: error instanceof Error ? error.message : 'Failed to delete note',
                        });
                        throw error;
                    }
                },

                togglePin: async (noteId: string) => {
                    try {
                        const result = await learningService.togglePin(noteId);
                        const { notes } = get();
                        set({
                            notes: notes.map((note) =>
                                note.id === noteId ? { ...note, isPinned: result.isPinned } : note
                            ),
                        });
                    } catch (error) {
                        set({
                            notesError: error instanceof Error ? error.message : 'Failed to toggle pin',
                        });
                        throw error;
                    }
                },

                searchNotes: async (courseId: string, query: string, limit = 50) => {
                    try {
                        const notes = await learningService.searchNotes(courseId, query, limit);
                        return notes;
                    } catch (error) {
                        set({
                            notesError: error instanceof Error ? error.message : 'Failed to search notes',
                        });
                        throw error;
                    }
                },

                // ============================================
                // Actions - Mode
                // ============================================

                setCurrentMode: (mode) => {
                    set({ currentMode: mode });
                },

                // ============================================
                // Actions - Utilities
                // ============================================

                reset: () => {
                    set({
                        currentCourseId: null,
                        progress: null,
                        progressLoading: false,
                        progressError: null,
                        vocabularyList: [],
                        reviewQueue: [],
                        vocabularyStats: null,
                        vocabularyLoading: false,
                        vocabularyError: null,
                        notes: [],
                        notesLoading: false,
                        notesError: null,
                        currentMode: 'full_text',
                    });
                },
            }),
            {
                name: 'learning-store',
                partialize: (state) => ({
                    currentCourseId: state.currentCourseId,
                    currentMode: state.currentMode,
                    // Don't persist loading states or errors
                }),
            }
        ),
        { name: 'LearningStore' }
    )
);

// ============================================
// Utility hooks for common operations
// ============================================

export const useLearningProgress = () => useLearningStore((state) => state.progress);
export const useVocabularyList = () => useLearningStore((state) => state.vocabularyList);
export const useReviewQueue = () => useLearningStore((state) => state.reviewQueue);
export const useNotes = () => useLearningStore((state) => state.notes);
export const useCurrentMode = () => useLearningStore((state) => state.currentMode);

export const useLearningActions = () => {
    const {
        setCurrentCourse,
        fetchProgress,
        updateProgress,
        fetchVocabulary,
        fetchReviewQueue,
        fetchVocabularyStats,
        reviewVocabulary,
        toggleFavorite,
        fetchNotes,
        createNote,
        updateNote,
        deleteNote,
        togglePin,
        searchNotes,
        setCurrentMode,
        reset,
    } = useLearningStore();

    return {
        setCurrentCourse,
        fetchProgress,
        updateProgress,
        fetchVocabulary,
        fetchReviewQueue,
        fetchVocabularyStats,
        reviewVocabulary,
        toggleFavorite,
        fetchNotes,
        createNote,
        updateNote,
        deleteNote,
        togglePin,
        searchNotes,
        setCurrentMode,
        reset,
    };
};
